import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { askForVisibility } from "../api.js";

/**
 * "Ask someone for access" (1a, 2t, 2u).
 *
 * The same form in all three places, because they are the same situation: a
 * person who cannot see an owner's availability and would like to. It tells
 * them nothing on the way out - see the enumeration rule in
 * docs/product-model.md - so the confirmation is worded to be true whether or
 * not anyone was found.
 */
export function AskForAccess({
  target,
  lead,
}: {
  /** Prefilled when we already know who they were trying to reach. */
  target?: string | undefined;
  lead?: string | undefined;
}) {
  const [who, setWho] = useState(target ?? "");
  const [requesterEmail, setRequesterEmail] = useState("");
  const [note, setNote] = useState("");
  const [asked, setAsked] = useState(false);

  const ask = useMutation({
    mutationFn: () =>
      askForVisibility({ target: who, requesterEmail, note: note || undefined }),
    onSuccess: () => {
      setAsked(true);
      setNote("");
    },
  });

  if (asked) {
    return (
      <div className="notice" role="status">
        <strong>That&rsquo;s with them now</strong>
        <span>
          If they use Meet With, your request is in their inbox. You won&rsquo;t hear
          from us either way — if they invite you, the invitation arrives from Meet
          With.
        </span>
        <button type="button" className="ghost" onClick={() => setAsked(false)}>
          Ask someone else
        </button>
      </div>
    );
  }

  return (
    <form
      onSubmit={(event) => {
        event.preventDefault();
        ask.mutate();
      }}
    >
      <p className="helper">
        {lead ??
          "Ask someone to let you see their availability. They decide; we only pass it along."}
      </p>
      <label className="field">
        <span>Their name or email address</span>
        <input
          type="text"
          required
          minLength={2}
          maxLength={320}
          placeholder="dana@company.example"
          value={who}
          onChange={(event) => setWho(event.target.value)}
        />
      </label>
      <label className="field">
        <span>Your email address</span>
        <input
          type="email"
          required
          maxLength={320}
          placeholder="you@yours.example"
          value={requesterEmail}
          onChange={(event) => setRequesterEmail(event.target.value)}
        />
      </label>
      <label className="field">
        <span>Why (optional)</span>
        <input
          type="text"
          maxLength={500}
          placeholder="Following up on the Halden proposal"
          value={note}
          onChange={(event) => setNote(event.target.value)}
        />
      </label>
      <button type="submit" className="secondary" disabled={ask.isPending}>
        {ask.isPending ? "Sending…" : "Send request"}
      </button>
      {ask.isError ? (
        <div className="notice notice-error" role="alert">
          <strong>That didn&rsquo;t send</strong>
          <span>{ask.error.message}</span>
        </div>
      ) : null}
    </form>
  );
}
